import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useStore } from '../context/StoreContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  RefreshCw, 
  FileText, 
  CreditCard, 
  Banknote,
  ArrowRightLeft,
  CheckCircle,
  Loader2,
  AlertTriangle,
  Zap,
  X,
  Building2,
  Wallet,
  Landmark,
  Ticket,
  TrendingUp,
  TrendingDown
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const ReconciliationPage = () => {
  const { stores, selectedStore, setSelectedStore } = useStore();
  const { isAdmin } = useAuth();
  
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [sourceType, setSourceType] = useState('bank');
  const [documentType, setDocumentType] = useState('invoice');
  
  // Bank accounts for filtering
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState('');
  
  // Credit cards for filtering
  const [creditCards, setCreditCards] = useState([]);
  const [selectedCreditCardId, setSelectedCreditCardId] = useState('');
  
  // Loans for filtering
  const [activeLoans, setActiveLoans] = useState([]);
  const [selectedLoanId, setSelectedLoanId] = useState('');
  
  // Data
  const [sourceTransactions, setSourceTransactions] = useState([]);
  const [documentTransactions, setDocumentTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Selection
  const [selectedSources, setSelectedSources] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  
  // Reconciliation
  const [varianceComment, setVarianceComment] = useState('');
  const [reconciling, setReconciling] = useState(false);
  
  // Auto-match
  const [autoMatchResults, setAutoMatchResults] = useState(null);
  const [showAutoMatch, setShowAutoMatch] = useState(false);
  const [autoMatching, setAutoMatching] = useState(false);
  const [pendingMatchIndex, setPendingMatchIndex] = useState(null); // Track which match needs variance comment
  const [autoMatchVarianceComment, setAutoMatchVarianceComment] = useState(''); // Comment for auto-match variance

  useEffect(() => {
    if (selectedStore?.id) {
      setSelectedStoreId(selectedStore.id);
    }
  }, [selectedStore]);

  // Fetch bank accounts when store changes
  useEffect(() => {
    if (selectedStoreId) {
      fetchBankAccounts();
      fetchCreditCards();
      fetchActiveLoans();
    }
  }, [selectedStoreId]);

  const fetchBankAccounts = async () => {
    try {
      const response = await axios.get(`${API}/api/reconciliation/bank-accounts`, {
        params: { store_id: selectedStoreId }
      });
      setBankAccounts(response.data);
      // Auto-select first bank if available
      if (response.data.length > 0 && !selectedBankId) {
        setSelectedBankId(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch bank accounts');
      setBankAccounts([]);
    }
  };

  const fetchCreditCards = async () => {
    try {
      const response = await axios.get(`${API}/api/credit-cards`, {
        params: { store_id: selectedStoreId }
      });
      setCreditCards(response.data);
      // Auto-select first card if available
      if (response.data.length > 0 && !selectedCreditCardId) {
        setSelectedCreditCardId(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch credit cards');
      setCreditCards([]);
    }
  };

  const fetchActiveLoans = async () => {
    try {
      const response = await axios.get(`${API}/api/loans/for-reconciliation/${selectedStoreId}`);
      setActiveLoans(response.data || []);
      // Auto-select first loan if available
      if (response.data.length > 0 && !selectedLoanId) {
        setSelectedLoanId(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch active loans');
      setActiveLoans([]);
    }
  };

  // Handle store selection change - also update global context
  const handleStoreChange = (storeId) => {
    setSelectedStoreId(storeId);
    setSelectedBankId(''); // Reset bank selection
    // Update global store context so header reflects the change
    const store = stores.find(s => s.id === storeId);
    if (store) {
      setSelectedStore(store);
    }
  };

  useEffect(() => {
    if (selectedStoreId) {
      fetchSourceTransactions();
      fetchDocumentTransactions();
      setAutoMatchResults(null);
      setShowAutoMatch(false);
    }
  }, [selectedStoreId, sourceType, documentType, selectedBankId, selectedCreditCardId]);

  // Auto-switch source to Bank when Bank Deposit is selected
  useEffect(() => {
    if (documentType === 'bank_deposit' && sourceType !== 'bank') {
      setSourceType('bank');
    }
    // Auto-switch to Bank when Credit Card is selected as document
    if (documentType === 'credit_card' && sourceType !== 'bank') {
      setSourceType('bank');
    }
    // Auto-switch to Bank when Loan Payment is selected as document
    if (documentType === 'loan_payment' && sourceType !== 'bank') {
      setSourceType('bank');
    }
  }, [documentType]);

  const fetchSourceTransactions = async () => {
    if (!selectedStoreId) return;
    setLoading(true);
    try {
      let endpoint = '';
      if (sourceType === 'bank') {
        endpoint = `${API}/api/reconciliation/bank-transactions?store_id=${selectedStoreId}&reconciled=false`;
        // Filter by selected bank account if one is selected
        if (selectedBankId) {
          endpoint += `&bank_account_id=${selectedBankId}`;
        }
      } else if (sourceType === 'paid_out') {
        endpoint = `${API}/api/reconciliation/paid-outs?store_id=${selectedStoreId}&reconciled=false`;
      } else if (sourceType === 'transfer') {
        // Fetch only Supplier Payment transfers
        endpoint = `${API}/api/reconciliation/transfers?store_id=${selectedStoreId}&reconciled=false&transfer_type=Supplier Payment`;
      } else if (sourceType === 'credit_card') {
        endpoint = `${API}/api/credit-cards/transactions?store_id=${selectedStoreId}&reconciled=false`;
        if (selectedCreditCardId) {
          endpoint += `&credit_card_id=${selectedCreditCardId}`;
        }
      } else if (sourceType === 'creditor_credit') {
        // Creditor credit transactions (outgoing - money lent out)
        endpoint = `${API}/api/reconciliation/creditor-transactions?store_id=${selectedStoreId}&direction=out`;
      } else if (sourceType === 'creditor_repayment') {
        // Creditor repayment transactions (incoming - money returned)
        endpoint = `${API}/api/reconciliation/creditor-transactions?store_id=${selectedStoreId}&direction=in`;
      }
      
      const response = await axios.get(endpoint);
      setSourceTransactions(response.data);
      setSelectedSources([]);
    } catch (error) {
      console.error('Failed to fetch source transactions');
      setSourceTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentTransactions = async () => {
    if (!selectedStoreId) return;
    try {
      let endpoint = '';
      if (documentType === 'invoice') {
        // Fetch both invoices AND voucher credit notes together
        const [invoicesRes, voucherCreditsRes] = await Promise.all([
          axios.get(`${API}/api/reconciliation/invoices?store_id=${selectedStoreId}&reconciled=false`),
          axios.get(`${API}/api/reconciliation/voucher-credit-notes?store_id=${selectedStoreId}&reconciled=false`)
        ]);
        
        const invoices = invoicesRes.data || [];
        const voucherCredits = (voucherCreditsRes.data || []).map(vc => ({
          ...vc,
          // Mark as voucher credit and make amount negative (reduces what we owe)
          is_voucher_credit: true,
          amount: -Math.abs(vc.amount), // Negative to offset against invoices
          display_amount: vc.amount, // Keep original for display
          invoice_number: vc.credit_note_number,
          supplier: vc.supplier_name,
          date: vc.date
        }));
        
        // Combine and sort by date (newest first)
        const combined = [...invoices, ...voucherCredits].sort((a, b) => {
          const dateA = new Date(a.date || a.invoice_date || '');
          const dateB = new Date(b.date || b.invoice_date || '');
          return dateB - dateA;
        });
        
        setDocumentTransactions(combined);
        return; // Exit early since we handled it
      } else if (documentType === 'commission') {
        endpoint = `${API}/api/reconciliation/commissions?store_id=${selectedStoreId}&reconciled=false`;
      } else if (documentType === 'bank_deposit') {
        // Fetch Bank Deposit transfers
        endpoint = `${API}/api/reconciliation/transfers?store_id=${selectedStoreId}&reconciled=false&transfer_type=Bank Deposit`;
      } else if (documentType === 'credit_card') {
        // Fetch credit card transactions for matching bank payments
        endpoint = `${API}/api/credit-cards/transactions?store_id=${selectedStoreId}&reconciled=false`;
        if (selectedCreditCardId) {
          endpoint += `&credit_card_id=${selectedCreditCardId}`;
        }
      } else if (documentType === 'voucher_credit') {
        // Fetch voucher credit notes for matching bank payments from suppliers
        endpoint = `${API}/api/reconciliation/voucher-credit-notes?store_id=${selectedStoreId}&reconciled=false`;
      } else if (documentType === 'loan_payment') {
        // For loan payments, we show active loans to select from
        // The document list will show the selected loan's info
        if (activeLoans.length > 0) {
          // Just show active loans as "documents" to reconcile
          const loansAsDocuments = activeLoans.map(loan => ({
            id: loan.id,
            type: 'loan',
            description: `${loan.lender} - ${loan.loan_type}`,
            amount: loan.monthly_payment,
            current_balance: loan.current_balance,
            date: loan.next_due_date || new Date().toISOString().split('T')[0]
          }));
          setDocumentTransactions(loansAsDocuments);
          setSelectedDocuments([]);
          return;
        }
      } else if (documentType === 'buying_group_invoice') {
        // Fetch reconciled Buying Group invoices (have delivery notes linked, not bank reconciled)
        endpoint = `${API}/api/reconciliation/buying-group-invoices?store_id=${selectedStoreId}&bank_reconciled=false`;
      } else if (documentType === 'fuel_credit_note') {
        // Fetch reconciled Fuel credit notes (have fuel transactions linked, not bank reconciled)
        endpoint = `${API}/api/reconciliation/fuel-credit-notes?store_id=${selectedStoreId}&bank_reconciled=false`;
      } else if (documentType === 'credit_request') {
        // Fetch unreconciled credit requests from creditors
        endpoint = `${API}/api/reconciliation/creditor-requests-for-recon?store_id=${selectedStoreId}&request_type=credit_request`;
      } else if (documentType === 'repayment_request') {
        // Fetch unreconciled repayment requests from creditors
        endpoint = `${API}/api/reconciliation/creditor-requests-for-recon?store_id=${selectedStoreId}&request_type=repayment_request`;
      }
      
      if (endpoint) {
        const response = await axios.get(endpoint);
        setDocumentTransactions(response.data);
      } else {
        setDocumentTransactions([]);
      }
      setSelectedDocuments([]);
    } catch (error) {
      console.error('Failed to fetch document transactions');
      setDocumentTransactions([]);
    }
  };

  const toggleSource = (txn) => {
    setSelectedSources(prev => {
      const exists = prev.find(s => s.id === txn.id);
      if (exists) {
        return prev.filter(s => s.id !== txn.id);
      } else {
        return [...prev, { id: txn.id, amount: Math.abs(txn.amount || txn.total_amount || 0) }];
      }
    });
  };

  const toggleDocument = (doc) => {
    setSelectedDocuments(prev => {
      const exists = prev.find(d => d.id === doc.id);
      if (exists) {
        return prev.filter(d => d.id !== doc.id);
      } else {
        // Preserve the sign of the amount (negative for credit notes)
        const amount = doc.total_amount || doc.amount || doc.commission_amount || 0;
        return [...prev, { id: doc.id, amount: amount }]; // Keep original sign
      }
    });
  };

  const totalSourceAmount = selectedSources.reduce((sum, s) => sum + s.amount, 0);
  // For documents, sum preserves negative amounts (credit notes reduce the total)
  const totalDocumentAmount = selectedDocuments.reduce((sum, d) => sum + d.amount, 0);
  const variance = Math.round((totalSourceAmount - totalDocumentAmount) * 100) / 100;

  const getButtonLabel = () => {
    if (sourceType === 'bank' && documentType === 'commission') {
      return 'Payment Received';
    } else if (sourceType === 'bank' && documentType === 'invoice') {
      return 'Invoice Paid';
    } else if (sourceType === 'bank' && documentType === 'bank_deposit') {
      return 'Deposit Matched';
    } else if (sourceType === 'bank' && documentType === 'voucher_credit') {
      return 'Voucher Credit Received';
    } else if (sourceType === 'bank' && documentType === 'buying_group_invoice') {
      return 'Buying Group Paid';
    } else if (sourceType === 'bank' && documentType === 'fuel_credit_note') {
      return 'Fuel Credit Received';
    } else if (sourceType === 'transfer' && documentType === 'invoice') {
      return 'Transfer Matched';
    } else if (sourceType === 'creditor_credit' && documentType === 'credit_request') {
      return 'Credit Request Matched';
    } else if (sourceType === 'creditor_repayment' && documentType === 'repayment_request') {
      return 'Repayment Matched';
    }
    return 'Reconcile';
  };

  const handleReconcile = async () => {
    if (selectedSources.length === 0 || selectedDocuments.length === 0) {
      toast.error('Select transactions from both sides');
      return;
    }
    
    if (Math.abs(variance) > 0.01 && !varianceComment.trim()) {
      toast.error('Please enter a comment explaining the variance');
      return;
    }
    
    setReconciling(true);
    try {
      // Special handling for loan payments
      if (documentType === 'loan_payment') {
        // For loan payments, we call a special endpoint
        const selectedLoan = selectedDocuments[0];
        const selectedBankTxn = selectedSources[0];
        
        await axios.post(`${API}/api/loans/reconcile-payment`, {
          loan_id: selectedLoan.id,
          bank_transaction_id: selectedBankTxn.id,
          amount: selectedBankTxn.amount,
          payment_date: selectedBankTxn.date || new Date().toISOString().split('T')[0]
        });
        
        toast.success('Loan payment reconciled! Loan balance updated.');
        
        // Refresh data
        setSelectedSources([]);
        setSelectedDocuments([]);
        setVarianceComment('');
        fetchSourceTransactions();
        fetchDocumentTransactions();
        fetchActiveLoans();
      } else {
        // Standard reconciliation - check if we have mixed invoice + voucher_credit documents
        const invoiceDocs = selectedDocuments.filter(d => !d.is_voucher_credit);
        const voucherCreditDocs = selectedDocuments.filter(d => d.is_voucher_credit);
        
        // If we have voucher credits mixed with invoices, reconcile them separately
        if (documentType === 'invoice' && voucherCreditDocs.length > 0) {
          // First reconcile invoices (if any)
          if (invoiceDocs.length > 0) {
            await axios.post(`${API}/api/reconciliation/reconcile`, {
              store_id: selectedStoreId,
              source_type: sourceType,
              document_type: 'invoice',
              source_transactions: selectedSources,
              document_transactions: invoiceDocs,
              variance_comment: varianceComment
            });
          }
          
          // Then reconcile voucher credits
          // Restore original positive amount for voucher credits
          const voucherCreditsForApi = voucherCreditDocs.map(vc => ({
            ...vc,
            amount: Math.abs(vc.amount) // Use positive amount for backend
          }));
          
          await axios.post(`${API}/api/reconciliation/reconcile`, {
            store_id: selectedStoreId,
            source_type: sourceType,
            document_type: 'voucher_credit',
            source_transactions: selectedSources,
            document_transactions: voucherCreditsForApi,
            variance_comment: varianceComment
          });
        } else {
          // Normal single-type reconciliation
          await axios.post(`${API}/api/reconciliation/reconcile`, {
            store_id: selectedStoreId,
            source_type: sourceType,
            document_type: documentType,
            source_transactions: selectedSources,
            document_transactions: selectedDocuments,
            variance_comment: varianceComment
          });
        }
        
        toast.success(`${getButtonLabel()} - Reconciliation completed!`);
        
        // Reset selections and refresh
        setSelectedSources([]);
        setSelectedDocuments([]);
        setVarianceComment('');
        fetchSourceTransactions();
        fetchDocumentTransactions();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reconciliation failed');
    } finally {
      setReconciling(false);
    }
  };

  // Auto-Match Logic - Two Pass Approach
  
  // Helper: Extract supplier name from bank description
  // Note: Backend now cleans descriptions, but keep this as fallback
  const extractSupplierName = (description) => {
    if (!description) return '';
    // Description should already be clean from backend, just trim
    return description.trim();
  };
  
  // Helper: Normalize supplier name for comparison
  const normalizeSupplier = (name) => {
    if (!name) return '';
    return name
      .toLowerCase()
      .replace(/\s+/g, '') // Remove all spaces
      .replace(/limited|ltd|llc|inc|plc|co\.?|company|ent|uk|group/gi, '') // Remove common suffixes
      .replace(/[^a-z0-9]/g, ''); // Keep only alphanumeric
  };
  
  // Helper: Calculate similarity between two strings (0-1)
  const calculateSimilarity = (str1, str2) => {
    if (!str1 || !str2) return 0;
    
    const s1 = normalizeSupplier(str1);
    const s2 = normalizeSupplier(str2);
    
    if (s1 === s2) return 1;
    if (s1.length === 0 || s2.length === 0) return 0;
    
    // Check if one contains the other
    if (s1.includes(s2) || s2.includes(s1)) {
      const containRatio = Math.min(s1.length, s2.length) / Math.max(s1.length, s2.length);
      return 0.85 + (containRatio * 0.15); // 0.85-1.0 range
    }
    
    // Check first N characters match (for typos at end)
    const checkLen = Math.min(4, Math.min(s1.length, s2.length));
    if (s1.substring(0, checkLen) === s2.substring(0, checkLen)) {
      // First few chars match - likely same supplier with typo
      // Calculate character overlap
      const chars1 = new Set(s1.split(''));
      const chars2 = new Set(s2.split(''));
      const intersection = [...chars1].filter(c => chars2.has(c)).length;
      const union = new Set([...chars1, ...chars2]).size;
      const jaccard = intersection / union;
      return 0.6 + (jaccard * 0.3); // 0.6-0.9 range
    }
    
    // Levenshtein-like: count matching characters in sequence
    let matches = 0;
    let j = 0;
    for (let i = 0; i < s1.length && j < s2.length; i++) {
      if (s1[i] === s2[j]) {
        matches++;
        j++;
      }
    }
    const seqRatio = matches / Math.max(s1.length, s2.length);
    
    // Character overlap (order-independent)
    const chars1 = new Set(s1.split(''));
    const chars2 = new Set(s2.split(''));
    const intersection = [...chars1].filter(c => chars2.has(c)).length;
    const overlapRatio = intersection / Math.max(chars1.size, chars2.size);
    
    return (seqRatio * 0.5 + overlapRatio * 0.5);
  };
  
  // Helper: Check if suppliers match (with fuzzy matching)
  const suppliersMatch = (sourceDesc, docSupplier) => {
    const similarity = calculateSimilarity(sourceDesc, docSupplier);
    
    // Return match info
    if (similarity >= 0.85) {
      return { match: true, type: 'exact', similarity };
    } else if (similarity >= 0.6) {
      return { match: true, type: 'fuzzy', similarity };
    }
    return { match: false, type: null, similarity };
  };

  const runAutoMatch = async () => {
    if (sourceTransactions.length === 0 || documentTransactions.length === 0) {
      toast.error('No transactions to match');
      return;
    }
    
    setAutoMatching(true);
    
    try {
      const matches = [];
      let unmatchedSources = [...sourceTransactions];
      let unmatchedDocs = [...documentTransactions];
      
      // Calculate acceptable variance: 3% of amount or £50, whichever is larger (min £5)
      const getAcceptableVariance = (amount) => {
        const percentVariance = amount * 0.03; // 3%
        return Math.max(5, Math.min(50, percentVariance));
      };
      
      // Helper: Check if dates are reasonable for matching
      // We allow invoice date to be up to 30 days after payment date
      // (to handle cases where invoices are entered into system after payment is made)
      const isDateValid = (invoiceDate, paymentDate) => {
        if (!invoiceDate || !paymentDate) return true; // If no dates, allow match
        try {
          const invDate = new Date(invoiceDate);
          const payDate = new Date(paymentDate);
          // Allow invoice date to be up to 30 days after payment date
          // This handles real-world scenarios where invoices are entered later
          const maxAllowedDate = new Date(payDate);
          maxAllowedDate.setDate(maxAllowedDate.getDate() + 30);
          return invDate <= maxAllowedDate;
        } catch {
          return true; // If date parsing fails, allow match
        }
      };
      
      // Sort by amount for better matching
      unmatchedSources.sort((a, b) => Math.abs(b.amount || b.total_amount || 0) - Math.abs(a.amount || a.total_amount || 0));
      
      // Debug: Log source and document transactions for troubleshooting
      console.log('=== Auto-Match Debug ===');
      console.log('Source transactions:', unmatchedSources.map(s => ({
        desc: s.description,
        amount: s.amount || s.total_amount
      })));
      console.log('Document transactions:', unmatchedDocs.map(d => ({
        supplier: d.supplier || d.supplier_name,
        amount: d.total_amount || d.amount || d.commission_amount,
        invoice: d.invoice_number
      })));
      
      // ============ PASS 1: Match by Supplier + Amount (Multi-Invoice) ============
      for (const source of [...unmatchedSources]) {
        const sourceAmount = Math.abs(source.amount || source.total_amount || 0);
        const rawSourceDesc = source.description || source.supplier || source.transfer_type || '';
        // Extract just the supplier name from bank description (before comma/reference)
        const sourceSupplierName = extractSupplierName(rawSourceDesc);
        const sourceDate = source.date;
        const acceptableVariance = getAcceptableVariance(sourceAmount);
        
        // Skip if no supplier info in source
        if (!sourceSupplierName || sourceSupplierName.length < 2) continue;
        
        // Get all docs from same supplier (using fuzzy match) AND with valid dates
        const sameSupplierDocs = unmatchedDocs.filter(doc => {
          const docSupplier = (doc.supplier || doc.supplier_name || '');
          if (!docSupplier || docSupplier.length < 2) return false;
          
          const matchResult = suppliersMatch(sourceSupplierName, docSupplier);
          if (!matchResult.match) return false;
          
          // Check date: invoice date must be on or before payment date
          const docDate = doc.date || doc.invoice_date || doc.commission_date;
          return isDateValid(docDate, sourceDate);
        });
        
        // Debug: Log supplier matching for this source
        if (sameSupplierDocs.length > 0) {
          console.log(`Source: "${sourceSupplierName}" (£${sourceAmount}) matched ${sameSupplierDocs.length} docs:`, 
            sameSupplierDocs.map(d => ({
              supplier: d.supplier || d.supplier_name,
              amount: d.total_amount || d.amount
            }))
          );
        }
        
        if (sameSupplierDocs.length === 0) continue;
        
        // Try to find the best combination of invoices that match the source amount
        // Strategy 1: Try single invoice matches first (most common case)
        // Strategy 2: Then try combinations for multi-invoice payments
        let bestMatch = null;
        let bestVariance = Infinity;
        
        // STRATEGY 1: Find single invoice that matches perfectly
        for (const doc of sameSupplierDocs) {
          const docAmount = Math.abs(doc.total_amount || doc.amount || doc.commission_amount || 0);
          const variance = Math.abs(sourceAmount - docAmount);
          
          if (variance <= acceptableVariance && variance < bestVariance) {
            const docSupplier = (doc.supplier || doc.supplier_name || '');
            const supplierMatchResult = suppliersMatch(sourceSupplierName, docSupplier);
            
            bestVariance = variance;
            bestMatch = {
              docs: [{
                doc,
                confidence: supplierMatchResult.type === 'exact' ? 'high' : 'fuzzy',
                amount: docAmount
              }],
              total: docAmount,
              variance: sourceAmount - docAmount,
              isFuzzy: supplierMatchResult.type === 'fuzzy'
            };
          }
        }
        
        // STRATEGY 2: If no single match found, try multi-invoice combinations
        if (!bestMatch && sameSupplierDocs.length > 1) {
          // Sort by amount (largest positive first, then negatives for credit notes)
          sameSupplierDocs.sort((a, b) => {
            const aAmount = a.total_amount || a.amount || a.commission_amount || 0;
            const bAmount = b.total_amount || b.amount || b.commission_amount || 0;
            return bAmount - aAmount;
          });
          
          // Try adding invoices one by one and track best combination match
          const candidateDocs = [];
          let runningTotal = 0;
          let isFuzzyMatch = false;
          
          for (const doc of sameSupplierDocs) {
            const docAmount = doc.total_amount || doc.amount || doc.commission_amount || 0;
            const docSupplier = (doc.supplier || doc.supplier_name || '');
            const supplierMatchResult = suppliersMatch(sourceSupplierName, docSupplier);
            
            if (supplierMatchResult.type === 'fuzzy') {
              isFuzzyMatch = true;
            }
            
            // Add this document to candidates
            candidateDocs.push({
              doc,
              confidence: supplierMatchResult.type === 'exact' ? 'high' : 'fuzzy',
              amount: docAmount
            });
            runningTotal += docAmount;
            
            // Check if current combination is a good match
            const variance = Math.abs(sourceAmount - Math.abs(runningTotal));
            if (variance <= acceptableVariance && variance < bestVariance) {
              bestVariance = variance;
              bestMatch = {
                docs: [...candidateDocs],
                total: runningTotal,
                variance: sourceAmount - Math.abs(runningTotal),
                isFuzzy: isFuzzyMatch
              };
            }
            
            // If we've exceeded source amount significantly, no point adding more positive invoices
            if (Math.abs(runningTotal) > sourceAmount + acceptableVariance && docAmount > 0) {
              break;
            }
          }
        }
        
        // If we found a good match
        if (bestMatch && bestMatch.docs.length > 0) {
          const hasFuzzyMatch = bestMatch.isFuzzy;
          
          matches.push({
            source,
            documents: bestMatch.docs.map(m => m.doc),
            confidence: hasFuzzyMatch ? 'fuzzy' : 'high',
            sourceAmount,
            documentsAmount: bestMatch.total,
            variance: bestMatch.variance,
            confirmed: false,
            rejected: false,
            matchType: hasFuzzyMatch ? 'fuzzy_supplier' : 'supplier_amount'
          });
          
          // Remove matched items
          unmatchedSources = unmatchedSources.filter(s => s.id !== source.id);
          for (const m of bestMatch.docs) {
            unmatchedDocs = unmatchedDocs.filter(d => d.id !== m.doc.id);
          }
        }
      }
      
      // ============ PASS 2: Match by Amount Only (for remaining) ============
      // This pass doesn't check supplier - just matches by amount
      // Keep it strict (only exact amount matches) since we can't verify supplier
      for (const source of [...unmatchedSources]) {
        const sourceAmount = Math.abs(source.amount || source.total_amount || 0);
        const sourceDate = source.date;
        const acceptableVariance = Math.max(5, sourceAmount * 0.02); // 2% or £5, stricter for amount-only
        
        // Find single invoice that matches exactly (within small variance) AND with valid date
        const exactMatch = unmatchedDocs.find(doc => {
          const docAmount = Math.abs(doc.total_amount || doc.amount || doc.commission_amount || 0);
          const docDate = doc.date || doc.invoice_date || doc.commission_date;
          
          // Check amount and date
          return Math.abs(sourceAmount - docAmount) <= acceptableVariance && isDateValid(docDate, sourceDate);
        });
        
        if (exactMatch) {
          const docAmount = exactMatch.total_amount || exactMatch.amount || exactMatch.commission_amount || 0;
          
          matches.push({
            source,
            documents: [exactMatch],
            confidence: 'medium',
            sourceAmount,
            documentsAmount: Math.abs(docAmount),
            variance: sourceAmount - Math.abs(docAmount),
            confirmed: false,
            rejected: false,
            matchType: 'amount_only'
          });
          
          // Remove matched items
          unmatchedSources = unmatchedSources.filter(s => s.id !== source.id);
          unmatchedDocs = unmatchedDocs.filter(d => d.id !== exactMatch.id);
        }
      }
      
      setAutoMatchResults({
        matches,
        unmatchedSources,
        unmatchedDocs
      });
      setShowAutoMatch(true);
      
      const exactMatches = matches.filter(m => m.confidence === 'high').length;
      const fuzzyMatches = matches.filter(m => m.confidence === 'fuzzy').length;
      const amountMatches = matches.filter(m => m.confidence === 'medium').length;
      
      toast.success(`Found ${matches.length} matches: ${exactMatches} exact, ${fuzzyMatches} similar supplier, ${amountMatches} amount only`);
    } catch (error) {
      toast.error('Auto-match failed');
      console.error(error);
    } finally {
      setAutoMatching(false);
    }
  };

  const confirmMatch = async (matchIndex, varianceCommentOverride = null) => {
    const match = autoMatchResults.matches[matchIndex];
    
    // If there's variance and no comment provided, prompt for comment
    if (Math.abs(match.variance) > 0.01 && varianceCommentOverride === null) {
      setPendingMatchIndex(matchIndex);
      setAutoMatchVarianceComment('');
      return; // Don't proceed - wait for user to enter comment
    }
    
    setReconciling(true);
    try {
      const sourceTransactionData = [{ id: match.source.id, amount: match.sourceAmount }];
      const documentTransactionData = match.documents.map(doc => ({
        id: doc.id,
        amount: Math.abs(doc.total_amount || doc.amount || doc.commission_amount || 0)
      }));
      
      // Use provided comment or auto-generate one
      const comment = varianceCommentOverride || 
        (Math.abs(match.variance) > 0.01 ? `Auto-matched with variance: £${match.variance.toFixed(2)}` : '');
      
      await axios.post(`${API}/api/reconciliation/reconcile`, {
        store_id: selectedStoreId,
        source_type: sourceType,
        document_type: documentType,
        source_transactions: sourceTransactionData,
        document_transactions: documentTransactionData,
        variance_comment: comment
      });
      
      // Update match as confirmed
      setAutoMatchResults(prev => ({
        ...prev,
        matches: prev.matches.map((m, i) => i === matchIndex ? { ...m, confirmed: true } : m)
      }));
      
      // Clear pending state
      setPendingMatchIndex(null);
      setAutoMatchVarianceComment('');
      
      toast.success('Match confirmed!');
    } catch (error) {
      toast.error('Failed to confirm match');
    } finally {
      setReconciling(false);
    }
  };

  const handleVarianceCommentSubmit = () => {
    if (!autoMatchVarianceComment.trim()) {
      toast.error('Please enter a comment explaining the variance');
      return;
    }
    confirmMatch(pendingMatchIndex, autoMatchVarianceComment);
  };

  const cancelVarianceComment = () => {
    setPendingMatchIndex(null);
    setAutoMatchVarianceComment('');
  };

  const rejectMatch = (matchIndex) => {
    const match = autoMatchResults.matches[matchIndex];
    
    // Move items back to unmatched
    setAutoMatchResults(prev => ({
      ...prev,
      matches: prev.matches.map((m, i) => i === matchIndex ? { ...m, rejected: true } : m),
      unmatchedSources: [...prev.unmatchedSources, match.source],
      unmatchedDocs: [...prev.unmatchedDocs, ...match.documents]
    }));
  };

  const confirmAllHighConfidence = async () => {
    // Only auto-confirm high confidence matches with NO variance
    const highConfidenceMatches = autoMatchResults.matches.filter(
      m => m.confidence === 'high' && !m.confirmed && !m.rejected && Math.abs(m.variance) <= 0.01
    );
    
    if (highConfidenceMatches.length === 0) {
      toast.info('No exact matches (zero variance) to auto-confirm. Matches with variance require individual comments.');
      return;
    }
    
    setReconciling(true);
    let confirmed = 0;
    
    for (let i = 0; i < autoMatchResults.matches.length; i++) {
      const match = autoMatchResults.matches[i];
      // Only confirm if high confidence AND no variance
      if (match.confidence === 'high' && !match.confirmed && !match.rejected && Math.abs(match.variance) <= 0.01) {
        try {
          const sourceTransactionData = [{ id: match.source.id, amount: match.sourceAmount }];
          const documentTransactionData = match.documents.map(doc => ({
            id: doc.id,
            amount: Math.abs(doc.total_amount || doc.amount || doc.commission_amount || 0)
          }));
          
          await axios.post(`${API}/api/reconciliation/reconcile`, {
            store_id: selectedStoreId,
            source_type: sourceType,
            document_type: documentType,
            source_transactions: sourceTransactionData,
            document_transactions: documentTransactionData,
            variance_comment: ''
          });
          
          confirmed++;
        } catch (error) {
          console.error('Failed to confirm match', error);
        }
      }
    }
    
    // Update only exact matches (no variance) as confirmed
    setAutoMatchResults(prev => ({
      ...prev,
      matches: prev.matches.map(m => 
        m.confidence === 'high' && !m.rejected && Math.abs(m.variance) <= 0.01 
          ? { ...m, confirmed: true } 
          : m
      )
    }));
    
    setReconciling(false);
    
    const remainingWithVariance = autoMatchResults.matches.filter(
      m => m.confidence === 'high' && !m.confirmed && !m.rejected && Math.abs(m.variance) > 0.01
    ).length;
    
    if (remainingWithVariance > 0) {
      toast.success(`${confirmed} exact matches confirmed! ${remainingWithVariance} matches with variance require individual comments.`);
    } else {
      toast.success(`${confirmed} matches confirmed!`);
    }
  };

  const getSourceLabel = () => {
    switch (sourceType) {
      case 'bank': return 'Bank Transactions';
      case 'paid_out': return 'Paid Outs';
      case 'transfer': return 'Supplier Payments';
      case 'credit_card': return 'Credit Card Transactions';
      case 'creditor_credit': return 'Creditor Credits (Out)';
      case 'creditor_repayment': return 'Creditor Repayments (In)';
      default: return 'Source';
    }
  };

  const getDocumentLabel = () => {
    switch (documentType) {
      case 'invoice': return 'Invoices & Voucher Credits';
      case 'commission': return 'Commission Statements';
      case 'bank_deposit': return 'Bank Deposits';
      case 'credit_card': return 'Credit Card Transactions';
      case 'loan_payment': return 'Active Loans';
      case 'buying_group_invoice': return 'Buying Group Invoices';
      case 'fuel_credit_note': return 'Fuel Credit Notes';
      case 'credit_request': return 'Credit Requests';
      case 'repayment_request': return 'Repayment Requests';
      default: return 'Documents';
    }
  };

  const pendingMatches = autoMatchResults?.matches.filter(m => !m.confirmed && !m.rejected) || [];
  const exactMatchCount = pendingMatches.filter(m => m.confidence === 'high').length;
  const fuzzyMatchCount = pendingMatches.filter(m => m.confidence === 'fuzzy').length;
  const amountMatchCount = pendingMatches.filter(m => m.confidence === 'medium').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
            Payment Reconciliation
          </h1>
          <p className="text-muted-foreground mt-1">
            Match payments with invoices, commissions, and bank deposits
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Row 1: Store + Actions */}
            <div className="flex flex-wrap items-end gap-4">
              {/* Store Selection */}
              <div className="space-y-2 min-w-[200px]">
                <Label>Store</Label>
                <Select value={selectedStoreId} onValueChange={handleStoreChange}>
                  <SelectTrigger data-testid="recon-store-select">
                    <SelectValue placeholder="Select store" />
                  </SelectTrigger>
                  <SelectContent>
                    {stores.map(store => (
                      <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Bank Account Selection - Only show when source is bank and there are multiple accounts */}
              {sourceType === 'bank' && bankAccounts.length > 0 && (
                <div className="space-y-2 min-w-[200px]">
                  <Label>Bank Account</Label>
                  <Select value={selectedBankId || 'all'} onValueChange={(v) => setSelectedBankId(v === 'all' ? '' : v)}>
                    <SelectTrigger data-testid="recon-bank-select">
                      <SelectValue placeholder="All bank accounts" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Bank Accounts</SelectItem>
                      {bankAccounts.map(bank => (
                        <SelectItem key={bank.id} value={bank.id}>
                          {bank.bank_name} {bank.account_number ? `(${bank.account_number})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Credit Card Selection - show when source or document is credit_card */}
              {(sourceType === 'credit_card' || documentType === 'credit_card') && creditCards.length > 0 && (
                <div className="space-y-1">
                  <Label>Credit Card</Label>
                  <Select value={selectedCreditCardId || 'all'} onValueChange={(v) => setSelectedCreditCardId(v === 'all' ? '' : v)}>
                    <SelectTrigger data-testid="recon-card-select">
                      <SelectValue placeholder="All credit cards" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Credit Cards</SelectItem>
                      {creditCards.map(card => (
                        <SelectItem key={card.id} value={card.id}>
                          {card.card_name} {card.card_last_four ? `(****${card.card_last_four})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex gap-2 ml-auto">
                {/* Auto-Match Button */}
                <Button 
                  variant="secondary" 
                  onClick={runAutoMatch}
                  disabled={autoMatching || sourceTransactions.length === 0 || documentTransactions.length === 0}
                >
                  {autoMatching ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Zap className="h-4 w-4 mr-2" />
                  )}
                  Find Matches
                </Button>

                {/* Refresh */}
                <Button 
                  variant="outline" 
                  onClick={() => { 
                    fetchSourceTransactions(); 
                    fetchDocumentTransactions();
                    setAutoMatchResults(null);
                    setShowAutoMatch(false);
                  }}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </div>

            {/* Row 2: Source and Document Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Source Type */}
              <div className="space-y-2">
                <Label>Source</Label>
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    variant={sourceType === 'paid_out' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSourceType('paid_out')}
                    disabled={documentType === 'bank_deposit' || documentType === 'credit_card'}
                    data-testid="source-paidout"
                  >
                    <Banknote className="h-4 w-4 mr-2" />
                    Paid Out
                  </Button>
                  <Button 
                    variant={sourceType === 'transfer' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSourceType('transfer')}
                    disabled={documentType === 'bank_deposit' || documentType === 'credit_card'}
                    data-testid="source-transfer"
                  >
                    <ArrowRightLeft className="h-4 w-4 mr-2" />
                    Transfers
                  </Button>
                  <Button 
                    variant={sourceType === 'credit_card' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSourceType('credit_card')}
                    disabled={documentType === 'bank_deposit' || documentType === 'credit_card'}
                    data-testid="source-credit-card"
                  >
                    <Wallet className="h-4 w-4 mr-2" />
                    Credit Card
                  </Button>
                  <Button 
                    variant={sourceType === 'bank' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSourceType('bank')}
                    data-testid="source-bank"
                  >
                    <Building2 className="h-4 w-4 mr-2" />
                    Bank
                  </Button>
                  <Button 
                    variant={sourceType === 'creditor_credit' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setSourceType('creditor_credit'); setDocumentType('credit_request'); }}
                    data-testid="source-creditor-credit"
                  >
                    <TrendingUp className="h-4 w-4 mr-2" />
                    Credits
                  </Button>
                  <Button 
                    variant={sourceType === 'creditor_repayment' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setSourceType('creditor_repayment'); setDocumentType('repayment_request'); }}
                    data-testid="source-creditor-repayment"
                  >
                    <TrendingDown className="h-4 w-4 mr-2" />
                    Repayments
                  </Button>
                </div>
              </div>

              {/* Document Type */}
              <div className="space-y-2">
                <Label>Document Type</Label>
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    variant={documentType === 'invoice' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDocumentType('invoice')}
                    disabled={sourceType === 'creditor_credit' || sourceType === 'creditor_repayment'}
                    data-testid="doc-invoice"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Invoices
                  </Button>
                  <Button 
                    variant={documentType === 'commission' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDocumentType('commission')}
                    disabled={sourceType === 'creditor_credit' || sourceType === 'creditor_repayment'}
                    data-testid="doc-commission"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Commission
                  </Button>
                  <Button 
                    variant={documentType === 'bank_deposit' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDocumentType('bank_deposit')}
                    disabled={sourceType === 'creditor_credit' || sourceType === 'creditor_repayment'}
                    data-testid="doc-bank-deposit"
                  >
                    <Building2 className="h-4 w-4 mr-2" />
                    Bank Deposit
                  </Button>
                  <Button 
                    variant={documentType === 'credit_card' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDocumentType('credit_card')}
                    disabled={sourceType === 'creditor_credit' || sourceType === 'creditor_repayment'}
                    data-testid="doc-credit-card"
                  >
                    <Wallet className="h-4 w-4 mr-2" />
                    Credit Card
                  </Button>
                  <Button 
                    variant={documentType === 'loan_payment' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDocumentType('loan_payment')}
                    disabled={sourceType === 'creditor_credit' || sourceType === 'creditor_repayment'}
                    data-testid="doc-loan-payment"
                  >
                    <Landmark className="h-4 w-4 mr-2" />
                    Loan Payment
                  </Button>
                  <Button 
                    variant={documentType === 'buying_group_invoice' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDocumentType('buying_group_invoice')}
                    disabled={sourceType === 'creditor_credit' || sourceType === 'creditor_repayment'}
                    data-testid="doc-buying-group"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Buying Group
                  </Button>
                  <Button 
                    variant={documentType === 'fuel_credit_note' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDocumentType('fuel_credit_note')}
                    disabled={sourceType === 'creditor_credit' || sourceType === 'creditor_repayment'}
                    data-testid="doc-fuel-credit"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Fuel Credit Note
                  </Button>
                  <Button 
                    variant={documentType === 'credit_request' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setDocumentType('credit_request'); setSourceType('creditor_credit'); }}
                    data-testid="doc-credit-request"
                  >
                    <TrendingUp className="h-4 w-4 mr-2" />
                    Credit Request
                  </Button>
                  <Button 
                    variant={documentType === 'repayment_request' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setDocumentType('repayment_request'); setSourceType('creditor_repayment'); }}
                    data-testid="doc-repayment-request"
                  >
                    <TrendingDown className="h-4 w-4 mr-2" />
                    Repayment Request
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Auto-Match Results */}
      {showAutoMatch && autoMatchResults && pendingMatches.length > 0 && (
        <Card className="border-primary/50 bg-primary/5">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Zap className="h-5 w-5 text-primary" />
                Auto-Matched Pairs ({pendingMatches.length})
              </CardTitle>
              <div className="flex items-center gap-2">
                {exactMatchCount > 0 && (
                  <Button 
                    size="sm" 
                    onClick={confirmAllHighConfidence}
                    disabled={reconciling}
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Confirm All Exact ({exactMatchCount})
                  </Button>
                )}
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowAutoMatch(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="flex gap-4 text-sm mt-2 flex-wrap">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-green-500"></span>
                Exact Match: {exactMatchCount}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                Similar Supplier (Review): {fuzzyMatchCount}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                Amount Only: {amountMatchCount}
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {autoMatchResults.matches.map((match, index) => {
                if (match.confirmed || match.rejected) return null;
                
                const borderColor = match.confidence === 'high' ? 'border-green-500' : 
                                   match.confidence === 'fuzzy' ? 'border-orange-500' : 'border-yellow-500';
                const bgColor = match.confidence === 'high' ? 'bg-green-500/10' : 
                               match.confidence === 'fuzzy' ? 'bg-orange-500/10' : 'bg-yellow-500/10';
                
                return (
                  <div key={index} className={`p-4 rounded-lg border-2 ${borderColor} ${bgColor}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        {/* Match Type Badge */}
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            match.confidence === 'high' 
                              ? 'bg-green-500/30 text-green-600' 
                              : match.confidence === 'fuzzy'
                              ? 'bg-orange-500/30 text-orange-600'
                              : 'bg-yellow-500/30 text-yellow-600'
                          }`}>
                            {match.matchType === 'supplier_amount' 
                              ? 'Exact Supplier + Amount' 
                              : match.matchType === 'fuzzy_supplier'
                              ? 'Similar Supplier (Please Review)'
                              : 'Amount Match Only'}
                          </span>
                        </div>
                        
                        {/* Source */}
                        <div className="flex items-center gap-2">
                          <CreditCard className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium">
                            {match.source.date} - {match.source.description || match.source.supplier || match.source.transfer_type}
                          </span>
                          <span className="font-mono font-bold">£{match.sourceAmount.toFixed(2)}</span>
                        </div>
                        
                        {/* Arrow */}
                        <div className="flex items-center gap-2 pl-6">
                          <span className="text-muted-foreground">↓ matches with</span>
                        </div>
                        
                        {/* Documents */}
                        {match.documents.map((doc, docIdx) => (
                          <div key={docIdx} className="flex items-center gap-2 pl-6">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">
                              {doc.invoice_number || doc.date} - {doc.supplier || doc.supplier_name || doc.transfer_type}
                            </span>
                            <span className="font-mono">
                              £{Math.abs(doc.total_amount || doc.amount || doc.commission_amount || 0).toFixed(2)}
                            </span>
                          </div>
                        ))}
                        
                        {/* Variance */}
                        {Math.abs(match.variance) > 0.01 && (
                          <div className="flex items-center gap-2 pl-6 text-amber-500 text-xs">
                            <AlertTriangle className="h-3 w-3" />
                            Variance: £{match.variance.toFixed(2)}
                          </div>
                        )}
                        
                        {/* Variance Comment Input - shows when this match is pending */}
                        {pendingMatchIndex === index && (
                          <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg space-y-2">
                            <label className="text-xs text-amber-500 font-medium">
                              Please explain the variance (£{Math.abs(match.variance).toFixed(2)}):
                            </label>
                            <Input
                              value={autoMatchVarianceComment}
                              onChange={(e) => setAutoMatchVarianceComment(e.target.value)}
                              placeholder="e.g., Bank charges, Rounding difference, Partial payment..."
                              className="bg-background"
                              data-testid="auto-match-variance-comment"
                              autoFocus
                            />
                            <div className="flex gap-2 justify-end">
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={cancelVarianceComment}
                              >
                                Cancel
                              </Button>
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={handleVarianceCommentSubmit}
                                disabled={!autoMatchVarianceComment.trim()}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Confirm with Comment
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Actions - hide when variance comment input is shown */}
                      {pendingMatchIndex !== index && (
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-green-500 text-green-500 hover:bg-green-500 hover:text-white"
                            onClick={() => confirmMatch(index)}
                            disabled={reconciling}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Confirm
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                            onClick={() => rejectMatch(index)}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Reconciliation Area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel - Source Transactions */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              {sourceType === 'bank' && <Building2 className="h-5 w-5" />}
              {sourceType === 'paid_out' && <Banknote className="h-5 w-5" />}
              {sourceType === 'transfer' && <ArrowRightLeft className="h-5 w-5" />}
              {sourceType === 'credit_card' && <Wallet className="h-5 w-5" />}
              {sourceType === 'creditor_credit' && <TrendingUp className="h-5 w-5 text-red-400" />}
              {sourceType === 'creditor_repayment' && <TrendingDown className="h-5 w-5 text-green-400" />}
              {getSourceLabel()}
              <span className="text-sm font-normal text-muted-foreground">
                ({sourceTransactions.length})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : sourceTransactions.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <p>No unreconciled transactions</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {sourceTransactions.map((txn) => {
                  const amount = Math.abs(txn.amount || txn.total_amount || 0);
                  const isSelected = selectedSources.some(s => s.id === txn.id);
                  
                  // For paid outs, combine supplier name and description like bank transactions
                  const isPaidOut = sourceType === 'paid_out';
                  const isCreditorTxn = sourceType === 'creditor_credit' || sourceType === 'creditor_repayment';
                  let displayTitle;
                  let displayType;
                  
                  if (isPaidOut) {
                    // Format: "SUPPLIER NAME, Description" or just "Description" if no supplier
                    if (txn.supplier_name && txn.description) {
                      displayTitle = `${txn.supplier_name}, ${txn.description}`;
                    } else if (txn.supplier_name) {
                      displayTitle = txn.supplier_name;
                    } else {
                      displayTitle = txn.description || 'Paid Out';
                    }
                    displayType = txn.paid_out_type_name || 'Paid Out';
                  } else if (isCreditorTxn) {
                    // For creditor transactions, show creditor name
                    displayTitle = txn.creditor_name || txn.description || 'Creditor Transaction';
                    displayType = txn.direction === 'out' ? 'Credit Out' : 'Payment In';
                  } else {
                    displayTitle = txn.description || txn.supplier || txn.transfer_type || 'Transaction';
                    displayType = txn.type;
                  }
                  
                  return (
                    <div 
                      key={txn.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => toggleSource(txn)}
                      data-testid={`source-${txn.id}`}
                    >
                      <Checkbox checked={isSelected} onCheckedChange={() => toggleSource(txn)} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">
                          {displayTitle}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {txn.date} {displayType && `• ${displayType}`}
                        </div>
                      </div>
                      <div className={`font-mono font-semibold ${
                        (txn.amount || 0) >= 0 ? 'text-green-500' : 'text-red-500'
                      }`}>
                        £{amount.toFixed(2)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right Panel - Documents */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              {documentType === 'bank_deposit' ? <Building2 className="h-5 w-5" /> : 
               documentType === 'credit_request' ? <TrendingUp className="h-5 w-5 text-red-400" /> :
               documentType === 'repayment_request' ? <TrendingDown className="h-5 w-5 text-green-400" /> :
               <FileText className="h-5 w-5" />}
              {getDocumentLabel()}
              <span className="text-sm font-normal text-muted-foreground">
                ({documentTransactions.length})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {documentTransactions.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <p>No unreconciled {
                  documentType === 'invoice' ? 'invoices or voucher credits' : 
                  documentType === 'commission' ? 'commissions' : 
                  documentType === 'credit_request' ? 'credit requests' :
                  documentType === 'repayment_request' ? 'repayment requests' :
                  'deposits'
                }</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {documentTransactions.map((doc) => {
                  const rawAmount = doc.total_amount || doc.amount || doc.commission_amount || 0;
                  const amount = Math.abs(rawAmount);
                  const isNegative = rawAmount < 0;
                  const isVoucherCredit = doc.is_voucher_credit;
                  const isCreditorRequest = documentType === 'credit_request' || documentType === 'repayment_request';
                  const isSelected = selectedDocuments.some(d => d.id === doc.id);
                  
                  return (
                    <div 
                      key={doc.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'
                      } ${isVoucherCredit ? 'bg-green-500/5' : ''}`}
                      onClick={() => toggleDocument(doc)}
                      data-testid={`doc-${doc.id}`}
                    >
                      <Checkbox checked={isSelected} onCheckedChange={() => toggleDocument(doc)} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate flex items-center gap-2">
                          {isCreditorRequest
                            ? `${doc.creditor_name || 'Creditor'} - ${doc.reference || 'No Ref'}`
                            : documentType === 'invoice' 
                            ? `${doc.invoice_number || 'INV'} - ${doc.supplier || doc.supplier_name || 'Supplier'}`
                            : documentType === 'commission'
                            ? `${doc.commission_type || 'Commission'} - ${doc.supplier_name || 'Supplier'}`
                            : `${doc.transfer_type || 'Deposit'} - ${doc.notes || doc.description || ''}`
                          }
                          {isVoucherCredit && (
                            <span className="px-1.5 py-0.5 text-xs bg-green-500/20 text-green-600 rounded">Voucher Credit</span>
                          )}
                          {isNegative && !isVoucherCredit && <span className="ml-1 text-xs text-red-500">(Credit)</span>}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {doc.date || doc.invoice_date || doc.commission_date}
                          {isVoucherCredit && doc.voucher_count && (
                            <span className="ml-2">({doc.voucher_count} vouchers)</span>
                          )}
                          {isCreditorRequest && doc.description && (
                            <span className="ml-2">• {doc.description}</span>
                          )}
                        </div>
                      </div>
                      <div className={`font-mono font-semibold ${
                        isVoucherCredit ? 'text-green-600' : 
                        isNegative ? 'text-red-500' : 
                        documentType === 'credit_request' ? 'text-red-400' :
                        documentType === 'repayment_request' ? 'text-green-400' :
                        'text-primary'
                      }`}>
                        {isVoucherCredit ? '-' : isNegative ? '-' : ''}£{amount.toFixed(2)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Sticky Bar */}
      <div className="sticky bottom-0 bg-background border-t border-border p-4 -mx-6 px-6 shadow-lg">
        <div className="flex items-center gap-6 flex-wrap">
          {/* Totals */}
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="text-xs text-muted-foreground">Source Selected</div>
              <div className="text-lg font-bold font-mono">£{totalSourceAmount.toFixed(2)}</div>
            </div>
            <div className="text-2xl text-muted-foreground">=</div>
            <div className="text-center">
              <div className="text-xs text-muted-foreground">Documents Selected</div>
              <div className="text-lg font-bold font-mono">£{totalDocumentAmount.toFixed(2)}</div>
            </div>
          </div>

          {/* Variance */}
          <div className={`text-center px-4 py-2 rounded-lg ${
            Math.abs(variance) > 0.01 
              ? 'bg-red-500/20 border border-red-500/50' 
              : 'bg-green-500/20 border border-green-500/50'
          }`}>
            <div className="text-xs text-muted-foreground">Variance</div>
            <div className={`text-lg font-bold font-mono ${
              Math.abs(variance) > 0.01 ? 'text-red-500' : 'text-green-500'
            }`}>
              {variance >= 0 ? '+' : ''}£{variance.toFixed(2)}
            </div>
          </div>

          {/* Variance Comment */}
          {Math.abs(variance) > 0.01 && (
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2 text-amber-500 text-xs mb-1">
                <AlertTriangle className="h-3 w-3" />
                Variance comment required
              </div>
              <Textarea
                value={varianceComment}
                onChange={(e) => setVarianceComment(e.target.value)}
                placeholder="Explain the variance..."
                className="h-10 min-h-[40px]"
                data-testid="variance-comment"
              />
            </div>
          )}

          {/* Reconcile Button */}
          <Button 
            size="lg"
            onClick={handleReconcile}
            disabled={reconciling || selectedSources.length === 0 || selectedDocuments.length === 0}
            className="ml-auto"
            data-testid="reconcile-btn"
          >
            {reconciling ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <CheckCircle className="h-4 w-4 mr-2" />
            )}
            {getButtonLabel()}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReconciliationPage;
